import { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";

import { mapColetasHistoricoPageFromApi, unwrapApiResult } from "./mpMappers";

function formatBrl(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(n);
}

export default function MonitoramentoPrecoMinhasColetasDialog({
  open,
  onClose,
  classes,
}) {
  const snackbar = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setExpandedId(null);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await Api.get(
          "coletas",
          "priceMonitoring",
          `?page=${page}&pageSize=${rowsPerPage}`,
          true,
        );
        if (cancelled) return;
        const raw = unwrapApiResult(res);
        const mapped = mapColetasHistoricoPageFromApi(raw);
        setRows(mapped.items);
        setTotalCount(mapped.totalCount);
        if (mapped.page !== page && mapped.page >= 1) {
          setPage(mapped.page);
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setTotalCount(0);
          const msg =
            typeof e === "string"
              ? e
              : e?.message || "Não foi possível carregar suas coletas.";
          snackbar.showMessage(msg, { severity: "error" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snackbar estável para exibir erro
  }, [open, page, rowsPerPage]);

  const toggleExpand = (idColeta) => {
    setExpandedId((prev) => (prev === idColeta ? null : idColeta));
  };

  const handleChangePage = (_, nextPageZeroBased) => {
    setPage(nextPageZeroBased + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.minhasColetasDialogPaper }}
    >
      <DialogTitle
        disableTypography
        className={classes.minhasColetasDialogTitle}
      >
        <Typography component="span" variant="h6" style={{ fontWeight: 700 }}>
          Minhas coletas
        </Typography>
        <IconButton aria-label="fechar" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          variant="body2"
          style={{ color: "#5c6570", marginBottom: 16 }}
        >
          Registros enviados por você, do mais recente para o mais antigo. Use a
          paginação abaixo para navegar. Fotos de concorrentes não são exibidas
          aqui; o envio original permanece no servidor.
        </Typography>
        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress color="primary" size={36} />
          </Box>
        )}
        {!loading && rows.length === 0 && totalCount === 0 && (
          <Typography variant="body2" style={{ color: "#5c6570" }}>
            Nenhuma coleta encontrada.
          </Typography>
        )}
        {!loading && (rows.length > 0 || totalCount > 0) && (
          <>
            <TableContainer className={classes.minhasColetasTableWrap}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: 44 }} />
                    <TableCell>Envio</TableCell>
                    <TableCell>Data coleta</TableCell>
                    <TableCell>Loja</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Preço Karsten</TableCell>
                    <TableCell>Tipo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const isOpen = expandedId === row.idColeta;
                    const envio = row.dataEnvioUtc
                      ? moment
                          .utc(row.dataEnvioUtc)
                          .local()
                          .format("DD/MM/YYYY HH:mm")
                      : "—";
                    return (
                      <Fragment key={row.idColeta}>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <IconButton
                              size="small"
                              aria-label={isOpen ? "recolher" : "expandir"}
                              onClick={() => toggleExpand(row.idColeta)}
                            >
                              {isOpen ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>{envio}</TableCell>
                          <TableCell>{row.dataColeta || "—"}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              noWrap
                              style={{ maxWidth: 200 }}
                            >
                              {row.loja?.razaoSocial || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.produto?.sku || "—"}</TableCell>
                          <TableCell>
                            {formatBrl(row.precoKarstenLoja)}
                          </TableCell>
                          <TableCell>{row.tipoColeta || "—"}</TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className={classes.minhasColetasDetailCell}
                            >
                              <Typography variant="subtitle2" gutterBottom>
                                Detalhes
                              </Typography>
                              <ul className={classes.minhasColetasDetailList}>
                                <li>
                                  <strong>Representante:</strong>{" "}
                                  {row.representante || "—"}
                                </li>
                                <li>
                                  <strong>Grupo econômico:</strong>{" "}
                                  {row.loja?.grupoEconomico || "—"}
                                </li>
                                <li>
                                  <strong>Produto:</strong>{" "}
                                  {row.produto?.nome || "—"}
                                </li>
                                <li>
                                  <strong>Concorrentes:</strong>
                                  {row.concorrentes?.length ? (
                                    <ul style={{ marginTop: 6 }}>
                                      {row.concorrentes.map((c, idx) => (
                                        <li key={`${row.idColeta}-c-${idx}`}>
                                          {c.nome}
                                          {c.encontrado
                                            ? ` — normal ${formatBrl(c.precoNormal)}`
                                            : " — não encontrado"}
                                          {c.emPromocao &&
                                          c.precoPromocional != null
                                            ? `; promo ${formatBrl(c.precoPromocional)}`
                                            : ""}
                                          {c.temFoto ? " (foto registrada)" : ""}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    " nenhum"
                                  )}
                                </li>
                              </ul>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              className={classes.minhasColetasPagination}
              component="div"
              count={totalCount}
              page={page - 1}
              onChangePage={handleChangePage}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 15, 25, 50]}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : to}`
              }
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

MonitoramentoPrecoMinhasColetasDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};
